import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AuditCta() {
  return (
    <section id="audit" className="relative overflow-hidden border-b border-slate-200 bg-slate-950 text-white">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" aria-hidden>
        <div className="absolute inset-0" style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }} />
      </div>
      <div className="absolute -top-32 -right-20 size-[520px] rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="label-mono text-cyan-300">/ 12 · Audit-Einstieg</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Lassen Sie uns Ihren Bestand prüfen.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              In 24 Minuten analysieren wir Ihre Fahrzeugdaten, Pflichtfelder, Feed-Qualität und
              SEO-Potenzial. Sie erhalten einen strukturierten Befund mit konkreten Handlungsoptionen.
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                "Datenqualitäts-Score pro Fahrzeug",
                "Feed-Validierung gegen Google-Vorgaben",
                "SEO-Befund pro Fahrzeugseite",
                "Konkrete Aufsatz-Empfehlung",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="size-4 text-cyan-400 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/audit">
                <Button data-testid="cta-audit-btn" data-track="cta_click" data-cta="audit" data-location="audit_cta_section" className="bg-white hover:bg-slate-100 text-slate-900 h-11 rounded-md text-sm pl-5 pr-4 inline-flex items-center gap-2">
                  Bestand prüfen lassen <ArrowUpRight className="size-4" />
                </Button>
              </Link>
              <Link to="/demo">
                <Button data-testid="cta-demo-btn" data-track="cta_click" data-cta="demo" data-location="audit_cta_section" variant="outline" className="border-slate-700 hover:border-slate-500 bg-transparent text-white h-11 rounded-md text-sm">
                  Demo ansehen
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white text-slate-900 border border-slate-200 rounded-xl ring-soft-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50/60">
                <p className="label-mono text-slate-500">audit-report.pdf</p>
                <p className="font-mono text-[11px] text-slate-500">v3.2 · adsFUX</p>
              </div>
              <div className="p-5">
                <p className="label-mono text-slate-500">Gesamt-Score</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <p className="font-display text-4xl font-bold tabular-nums">72</p>
                  <p className="text-sm text-slate-500">/ 100</p>
                  <span className="ml-auto text-[11px] font-mono uppercase tracking-[0.18em] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                    Auffällig
                  </span>
                </div>

                <div className="mt-5 space-y-3">
                  <ScoreRow label="Pflichtfelder" value={92} color="emerald" />
                  <ScoreRow label="Bilder & Medien" value={68} color="amber" />
                  <ScoreRow label="Feed-Validität" value={84} color="cyan" />
                  <ScoreRow label="SEO-Landingpages" value={41} color="rose" />
                  <ScoreRow label="Strukturierte Daten" value={29} color="rose" />
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="label-mono text-slate-500">Empfehlung</p>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    Priorität auf SEO-Landingpage-Aufbau + Schema-Daten. Geschätztes
                    Sichtbarkeits-Plus: +34% in 60 Tagen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScoreRow({ label, value, color }) {
  const c = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    cyan: "bg-cyan-500",
    rose: "bg-rose-500",
  }[color];
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] font-mono text-slate-600">
        <span>{label}</span>
        <span className="tabular-nums">{value}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${c}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
